import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './ResumeUploadModal.css';

const ResumeUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const { t } = useTranslation();
  const { uploadResume } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setError(null);
    
    if (selectedFile) {
      // Validate file type
      if (selectedFile.type !== 'application/pdf') {
        setError(t('resumeModal.errors.pdfOnly'));
        return;
      }
      
      // Validate file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError(t('resumeModal.errors.fileSize'));
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError(t('resumeModal.errors.noFile'));
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await uploadResume(file);
      
      if (result.processing_status === 'completed') {
        setSuccess(true);
        setTimeout(() => {
          onUploadSuccess(result);
        }, 1500);
      } else if (result.processing_status === 'failed') {
        setError(t('resumeModal.errors.uploadFailed'));
      } else {
        // Still processing
        setSuccess(true);
        setTimeout(() => {
          onUploadSuccess(result);
        }, 1500);
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{t('resumeModal.title')}</h2>
          {!uploading && !success && (
            <button className="close-btn" onClick={handleClose}>
              ×
            </button>
          )}
        </div>

        <div className="modal-body">
          {!success ? (
            <>
              <p>{t('resumeModal.description')}</p>

              <div className="upload-section">
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    id="resume-file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="file-input"
                  />
                  <label htmlFor="resume-file" className="file-input-label">
                    {file ? file.name : t('resumeModal.chooseFile')}
                  </label>
                </div>

                <div className="upload-requirements">
                  <h4>{t('resumeModal.requirements.title')}</h4>
                  <ul>
                    <li>{t('resumeModal.requirements.pdfOnly')}</li>
                    <li>{t('resumeModal.requirements.maxSize')}</li>
                    <li>{t('resumeModal.requirements.aiProcessing')}</li>
                  </ul>
                </div>

                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}

                <div className="modal-actions">
                  <button 
                    onClick={handleClose} 
                    className="cancel-btn"
                    disabled={uploading}
                  >
                    {t('resumeModal.buttons.cancel')}
                  </button>
                  <button 
                    onClick={handleUpload} 
                    className="upload-btn"
                    disabled={!file || uploading}
                  >
                    {uploading ? t('resumeModal.buttons.uploading') : t('resumeModal.buttons.upload')}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="success-section">
              <div className="success-icon">✅</div>
              <h3>{t('resumeModal.success.title')}</h3>
              <p>{t('resumeModal.success.message')}</p>
              <div className="loading-spinner"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumeUploadModal;